import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Send } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Loading } from "../../components/shared";

export default function ResidentFormalRequestDetail() {
  const { requestId } = useParams();
  const [data, setData] = useState(null);
  const [response, setResponse] = useState("");
  const load = useCallback(() =>
    api
      .get(`/resident-requests/requests/${requestId}`)
      .then((result) => setData(result.data))
      .catch(() => setData({ missing: true })),
  [requestId]);
  useEffect(() => {
    load();
  }, [load]);
  if (!data) return <Loading />;
  if (data.missing)
    return (
      <p data-testid="resident-request-missing">
        This request is not available.
      </p>
    );
  const submitResponse = async () => {
    try {
      await api.post(`/resident-requests/requests/${requestId}/respond`, {
        body: response,
      });
      toast.success("Clarification response sent");
      setResponse("");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send response");
    }
  };
  return (
    <article data-testid="resident-formal-request-detail">
      <Link
        className="text-sm font-semibold text-blue-700"
        data-testid="resident-request-back"
        to="/resident/requests"
      >
        ← All requests
      </Link>
      <header className="mt-5 border-b border-slate-200 pb-5">
        <p className="text-xs font-semibold text-blue-700">
          {data.request.request_number} · {data.request.status}
        </p>
        <h1 className="mt-2 font-display text-3xl font-bold">
          {data.request.request_type}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Unit {data.request.unit || "—"}
        </p>
      </header>
      <section className="mt-6" data-testid="resident-request-submission">
        <h2 className="font-display text-xl font-semibold">
          Submitted information
        </h2>
        <dl className="mt-3 grid gap-2 text-sm">
          {data.submission.fields.map((field) => (
            <div
              className="border border-slate-200 bg-white p-3"
              key={field.key}
            >
              <dt className="font-semibold">{field.label}</dt>
              <dd className="mt-1 text-slate-700">
                {String(data.submission.values[field.key] ?? "—")}
              </dd>
            </div>
          ))}
        </dl>
      </section>
      <section className="mt-6" data-testid="resident-request-messages">
        <h2 className="font-display text-xl font-semibold">Updates</h2>
        <div className="mt-3 space-y-2">
          {data.messages.map((message) => (
            <div
              className="border border-slate-200 bg-white p-3 text-sm"
              key={message.message_id}
            >
              <strong>
                {message.kind === "CLARIFICATION"
                  ? "Society clarification"
                  : "Your response"}
              </strong>
              <p className="mt-1 whitespace-pre-wrap">{message.body}</p>
            </div>
          ))}
        </div>
        {data.request.status === "CLARIFICATION_REQUIRED" && (
          <div className="mt-4">
            <Textarea
              data-testid="request-clarification-response"
              onChange={(event) => setResponse(event.target.value)}
              value={response}
            />
            <Button
              className="mt-2"
              data-testid="request-send-response"
              onClick={submitResponse}
              size="sm"
            >
              <Send className="h-4 w-4" />
              Respond
            </Button>
          </div>
        )}
      </section>
      <section className="mt-6" data-testid="resident-request-files">
        <h2 className="font-display text-xl font-semibold">Documents</h2>
        {data.files.map((file) => (
          <a
            className={
              "mt-2 flex items-center justify-between border border-slate-200 bg-white " +
              "p-3 text-sm text-blue-700"
            }
            data-testid={`request-file-${file.file_id}`}
            href={`${API}/resident-requests/files/${file.file_id}/download`}
            key={file.file_id}
            target="_blank"
            rel="noreferrer"
          >
            {file.filename}
            <Download className="h-4 w-4" />
          </a>
        ))}
      </section>
    </article>
  );
}
